import { useEffect, useState } from "react";

import { carregarExplicacao, type Explicacao } from "../api";
import { decimal, moedaCheia } from "../leitura.js";
import { Vazio } from "./Cartao";

/**
 * A explicação de uma recomendação, tal como o motor calculou (CLAUDE.md §3:
 * "a explicação é o cálculo"). Este componente não soma, não arredonda e não
 * decide o que mostrar além do que veio pronto — só formata número para
 * leitura, do mesmo jeito que `leitura.js` já faz para o painel executivo.
 */
function formatar(valor: number, unidade: string): string {
  if (unidade === "brl") return moedaCheia(valor);
  if (unidade === "%") return `${decimal(valor * 100)}%`;
  return unidade ? `${decimal(valor)} ${unidade}` : decimal(valor);
}

export function PainelExplicacao({ id }: { id: string }) {
  const [dado, setDado] = useState<Explicacao | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    carregarExplicacao(id)
      .then((d) => { if (vivo) setDado(d); })
      .catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; };
  }, [id]);

  if (erro) return <Vazio>Não foi possível carregar a explicação.</Vazio>;
  if (!dado) return <p className="nota">Carregando explicação…</p>;

  const { explicacao } = dado;

  return (
    <div className="explicacao">
      <p className="explicacao-titulo">{explicacao.titulo}</p>
      <p className="explicacao-formula">{explicacao.formula}</p>

      <table className="tabela">
        <thead>
          <tr><th>Fator</th><th>Valor</th><th>De onde veio</th></tr>
        </thead>
        <tbody>
          {explicacao.fatores.map((f) => (
            <tr key={f.chave}>
              <td>{f.rotulo}</td>
              <td className="num">{formatar(f.valor, f.unidade)}</td>
              <td className="codigo">{f.origem || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {explicacao.passos.length > 0 && (
        <ol className="explicacao-passos">
          {explicacao.passos.map((p, i) => (
            <li key={`${p.destino}-${i}`}>
              {p.rotulo || p.destino}: {p.operacao}({p.entradas.join(", ")}) = {formatar(p.resultado, "")}
            </li>
          ))}
        </ol>
      )}

      {explicacao.regras_aplicadas.length > 0 && (
        <ul className="explicacao-regras">
          {explicacao.regras_aplicadas.map((r) => (
            <li key={r.regra}><span className="codigo">{r.regra}</span> — {r.efeito}</li>
          ))}
        </ul>
      )}

      {explicacao.evidencias.length > 0 && (
        <details className="explicacao-evidencias">
          <summary>Evidências ({explicacao.evidencias.length})</summary>
          <ul>
            {explicacao.evidencias.map((e, i) => (
              <li key={i}>{e.descricao}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
